import type { GraphModification, ConceptEvent, Graph } from "@lr-notitia/notitia-engineer/schemas";
import { Ref, ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";
import * as Metrics from "../metrics";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const conceptEvents = (graph: Ref<Graph>) =>
	computed<ConceptEvent[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "event" && node.layer === "concept"
			) as ConceptEvent[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function conceptEvent(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => {
			Metrics.recordAccess(id);
			return graph.value.nodes[id] as ConceptEvent | undefined;
		},
		add: (data: { refersTo: string; localTerminology: Terminology }) => add(domainId, graph, data),
		update: (event: ConceptEvent) => update(domainId, graph, event),
		remove: (id: string) => remove(domainId, graph, id),
		openedId,
		opened: computed<ConceptEvent | undefined>(
			() => graph.value.nodes[openedId?.value ?? ""] as ConceptEvent | undefined
		),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	data: { refersTo: string; localTerminology: Terminology }
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.events()
			.post(data)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, event: ConceptEvent): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[event.id]) return reject();
		api()
			.domain(domainId.value)
			.event(event.id)
			.put(event)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.event(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

const openedId = ref<string | undefined>(undefined);
